/**
 * Keep bearer tokens that ride in a URL out of browser analytics (SCH-3420).
 *
 * Some public pages take their credential in the URL: the my-tickets signed
 * link, the door check-in link, the chat email unsubscribe page. PostHog
 * records the page URL on every event (`$current_url`, `$pathname`,
 * `$referrer`, and the `$initial_*` person properties), and Datadog RUM records
 * `view.url`, `view.referrer` and the URL of every fetch the page makes, which
 * for these pages is the backend read carrying the same token. Both are
 * retained and readable by far more people than the token's holder, so the
 * token is replaced before an event leaves the browser.
 *
 * The backend keeps its own list for its logs and spans
 * (`apps/backend/src/utils/redact-token-path.ts`); the two cover different URL
 * shapes (web pages here, plus the backend reads those pages fetch).
 *
 * A plain string rewrite: works on a full URL, a bare path, or anything else,
 * and never throws.
 */

const REDACTED = '[redacted]'
const SEGMENT = '[^/?#]+'

/** Start of a path: string start, or right after a URL's origin. */
const PATH_START = '^((?:[a-z][a-z0-9+.-]*:)?(?://[^/?#]*)?'

/** `(prefix kept)(token replaced)`. School slugs are kept: they are public. */
const TOKEN_URL_PATTERNS: readonly RegExp[] = [
  // Web page: /my-tickets/<school>/t/<token>
  new RegExp(`${PATH_START}/my-tickets/${SEGMENT}/t/)${SEGMENT}`, 'i'),
  // Web page, deprecated: /my-tickets/<school>/<schoolUserId> (the CUID is the credential)
  new RegExp(`${PATH_START}/my-tickets/${SEGMENT}/)(?!t(?:[/?#]|$))${SEGMENT}`, 'i'),
  // Web page: /check-in/<school>/<token>
  new RegExp(`${PATH_START}/check-in/${SEGMENT}/)${SEGMENT}`, 'i'),
  // Backend reads those pages fetch (RUM resource URLs)
  new RegExp(`(/by-slug/${SEGMENT}/my-tickets/t/)${SEGMENT}`, 'gi'),
  new RegExp(`(/by-slug/${SEGMENT}/my-tickets/)(?!t(?:[/?#]|$))${SEGMENT}`, 'gi'),
  new RegExp(`(/by-slug/${SEGMENT}/check-in/)${SEGMENT}`, 'gi'),
  new RegExp(`(/chat-email/unsubscribe/)${SEGMENT}`, 'gi'),
  // A `token` query parameter (the unsubscribe confirm page: /unsubscribe/chat-email?token=...)
  /([?&]token=)[^&#]+/gi
]

/** The URL with every known token segment replaced. Anything else is returned unchanged. */
export function redactTokenUrl(url: string): string {
  return TOKEN_URL_PATTERNS.reduce((acc, pattern) => acc.replace(pattern, `$1${REDACTED}`), url)
}

/** PostHog properties that carry a page URL or path. */
const POSTHOG_URL_PROPERTIES = [
  '$current_url',
  '$pathname',
  '$referrer',
  '$initial_current_url',
  '$initial_pathname',
  '$initial_referrer',
  '$prev_pageview_pathname'
] as const

function redactUrlProperties(properties: Record<string, unknown> | undefined): void {
  if (!properties) return
  for (const key of POSTHOG_URL_PROPERTIES) {
    const value = properties[key]
    if (typeof value === 'string') properties[key] = redactTokenUrl(value)
  }
}

/** The fields of a PostHog `CaptureResult` this touches. */
interface PostHogCaptureLike {
  properties?: Record<string, unknown>
  $set?: Record<string, unknown>
  $set_once?: Record<string, unknown>
}

/**
 * A posthog-js `before_send` step: rewrites the URL properties on the event and
 * on the person properties it sets. Never drops an event.
 */
export function redactTokenUrlsInPostHogEvent<T extends PostHogCaptureLike>(event: T | null): T | null {
  if (!event) return event
  redactUrlProperties(event.properties)
  redactUrlProperties(event.$set)
  redactUrlProperties(event.$set_once)
  return event
}

/** The fields of a Datadog RUM event `beforeSend` may rewrite and this touches. */
interface RumEventLike {
  view?: { url?: string; referrer?: string }
  resource?: { url?: string }
  error?: { resource?: { url?: string } }
}

/** Rewrite the token-bearing URLs on a RUM (or browser-logs) event in place. */
export function redactTokenUrlsInRumEvent(event: RumEventLike): void {
  if (typeof event.view?.url === 'string') event.view.url = redactTokenUrl(event.view.url)
  if (typeof event.view?.referrer === 'string') event.view.referrer = redactTokenUrl(event.view.referrer)
  if (typeof event.resource?.url === 'string') event.resource.url = redactTokenUrl(event.resource.url)
  if (typeof event.error?.resource?.url === 'string') {
    event.error.resource.url = redactTokenUrl(event.error.resource.url)
  }
}
